import './Global.scss';
import 'extensions/fasten-header';
import NavigationMenu from 'extensions/navigation-menu';
import ContinueShopping from 'extensions/continue-shopping';
import {PageManager} from '@mvps-genesis/bootstrap';
import {GlobalMessageContainer} from 'extensions/messages';
import MiniBasket from 'extensions/mini-basket';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
// Import {SkipToSection} from '@mvps-genesis/accessibility';
import EventBus from '@mvps-genesis/event-bus';
import 'focus-visible';

export default class Global extends PageManager {
	// eslint-disable-next-line max-lines-per-function
	constructor (pageContext) {

		super(pageContext);

		this.initLazyload();

		/*
		 * SkipToSection.createFromArray([
		 * 	{
		 * 		label: 'Menu',
		 * 		targetElement: '.js-navigation-menu'
		 * 	},
		 * 	{
		 * 		label: 'Search',
		 * 		targetElement: '[itemprop="query-input"]'
		 * 	},
		 * 	{
		 * 		label: 'Content',
		 * 		targetElement: '.t-main-content-element'
		 * 	},
		 * 	{
		 * 		label: 'Footer',
		 * 		targetElement: '.t-site-footer'
		 * 	}
		 * ]);
		 */

		this.initSearchField();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});

		/**
		 * Initialize the Mega Menu extension
		 */
		new NavigationMenu({
			drawers: '.js-navigation-menu__drawer',
			mainMenu: '.js-navigation-menu',
			mainMenuCloseButtons: '.js-navigation-menu__close',
			mainMenuOpenButtons: '.js-navigation-menu__open',
			menuItems: '.js-navigation-menu__link',
			parentMenus: '.js-navigation-menu__has-child',
			showPreviousMenuButtons: '.js-navigation-menu__previous'
		});

		this.initializeHTMLFormValidator();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.accordionPolyfill();

		this.detectIE();

	}

	onReady () {

		this.criticalCSS();

		/**
		 * Initialize the Mini-Basket extension
		 */
		new MiniBasket();

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();

		this.initMiniModal();
		this.showRelated();
		this.triggerPrint();
		this.addToCartListener();
		this.initCarousels();
		this.initConfirmAction();

	}

	async initSearchField () {
		const {default: InitSearchFields} = await import('custom-extensions/searchfield');

		new InitSearchFields(this.pageContext);
	}

	criticalCSS () {
		let addStylesNode = document.getElementById('js-main-css');

		if (!addStylesNode) {
			return;
		}

		const loadDeferredStyles = function () {
			addStylesNode.setAttribute('rel', 'stylesheet');
		};

		const raf = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;

		if (raf) {
			raf(function () {
				window.setTimeout(loadDeferredStyles, 0);
			});
		} else {
			window.addEventListener('load', loadDeferredStyles);
		}
	}

	async initLazyload () {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		window.lazySizesConfig = window.lazySizesConfig || {};

		// Module
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes');
	}

	async accordionPolyfill () {

		/**
		 * Initialize Accordion Polyfill
		 */

		// Elements
		const accordionElements = document.getElementsByClassName('x-accordion');

		if (accordionElements.length === 0) {
			return;
		}

		// Module
		await import('details-polyfill');
	}

	async initializeHTMLFormValidator () {

		/**
		 * Initialize Inline Form Validations
		 */

		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		forms.forEach(function (form) {
			form.HTMLFormValidator = new HTMLFormValidator({
				formElement: form,
				wrapperSelector: '.c-form-list__item'
			});
		});
	}

	async initMiniModal () {

		/**
		 * Mini Modal
		 */
		// Elements
		const miniModalElements = Array.from(document.querySelectorAll('[data-mini-modal]'));

		if (miniModalElements.length === 0) {
			return;
		}

		// Module
		const {default: minimodal} = await import('extensions/minimodal');

		// Initiate the modal for each element
		miniModalElements.forEach(element => {
			minimodal(element).init();
		});
	}

	showRelated () {

		/**
		 * Toggle the `is-open` state of the global account box.
		 */

		var trigger = document.querySelector('[data-hook="show-related"]');

		if (trigger) {
			var relatedTarget = document.querySelector('[data-hook="' + trigger.getAttribute('data-target') + '"]');

			trigger.addEventListener('click', function (event) {
				event.preventDefault();
				relatedTarget.classList.toggle('is-open');
			}, false);

			document.addEventListener('mousedown', function (event) {
				if (relatedTarget.classList.contains('is-open')) {
					if (!relatedTarget.contains(event.target) && event.target !== trigger) {
						relatedTarget.classList.toggle('is-open');
						event.preventDefault();
					}
				}
			}, false);

			window.addEventListener('keydown', function (event) {
				if (event.defaultPrevented) {
					return;
				}

				switch (event.key) {
				case 'Escape':
					if (relatedTarget.classList.contains('is-open')) {
						relatedTarget.classList.toggle('is-open');
					}
					break;
				default:
					return;
				}

				event.preventDefault();
			}, true);
		}

	}

	triggerPrint () {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			printTriggers.forEach(element => {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			});
		}

	}

	addToCartListener () {
		EventBus.addEventListener('MVPS:addToCart', (event) => {
			// GTM addToCart Event
			if (event.detail && event.detail.success && event.detail.form) {
				const priceValue = event.detail.form.querySelector('#price-value');

				if (priceValue) {
					const gtmPrice = Number(priceValue.innerHTML.replace(/[^0-9.-]+/g, ''));

					window.dataLayer.push({
						ecommerce: {
							add: {
								products: [
									{
										id: event.detail.form.getAttribute('data-gtm-code'),
										name: event.detail.form.getAttribute('data-gtm-name'),
										price: gtmPrice,
										product_id: parseInt(event.detail.form.getAttribute('data-gtm-productid'), 10),
										quantity: parseInt(event.detail.form.querySelector('input[name="Quantity"]').value, 10)
									}
								]
							},
							currencyCode: 'USD'
						},
						event: 'addToCart'
					});
				}
			}
		});
	}

	async initCarousels () {
		const carouselElements = document.getElementsByClassName('js-carousel');

		if (carouselElements.length === 0) {
			return;
		}

		const {initCarousels} = await import('custom-extensions/splide-carousel');

		/**
		 * Initialize carousels
		 */
		initCarousels();
	}

	async initConfirmAction () {
		const confirmElements = document.getElementsByClassName('js-confirm-action');

		if (confirmElements.length === 0) {
			return;
		}

		const {default: ConfirmAction} = await import('extensions/confirm-action');

		new ConfirmAction();
	}

	async detectIE () {
		if (!(navigator.userAgent.toUpperCase().indexOf('TRIDENT/') !== -1 || navigator.userAgent.toUpperCase().indexOf('MSIE') !== -1)) {
			return;
		}

		await import('custom-extensions/IE11/index.scss');
	}
}
